import { create } from 'zustand'
import * as authApi from '../lib/api/auth'
import { getAuthToken } from '../lib/api/client'
import { matchDemoLogin, storedDemo, rememberDemo, clearDemo } from '../lib/demo'
import type { PublicUser } from '../lib/api/types'

interface AuthState {
  user: PublicUser | null
  status: 'unknown' | 'authenticated' | 'guest'
  /** Validate the stored token on app start. */
  hydrate: () => Promise<void>
  /** Login OR register (same endpoint). Returns ok + whether the account was just created. */
  signIn: (username: string, password: string) => Promise<{ ok: boolean; isNew: boolean }>
  signOut: () => Promise<void>
  refresh: () => Promise<void>
  /** Replace the cached user (e.g. after a profile update). */
  setUser: (user: PublicUser | null) => void
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  status: 'unknown',

  hydrate: async () => {
    const demo = storedDemo() // restore a demo session without touching the backend
    if (demo) {
      set({ user: demo.user, status: 'authenticated' })
      return
    }
    if (!getAuthToken()) {
      set({ status: 'guest', user: null })
      return
    }
    const user = await authApi.getCurrentUser()
    set(user ? { user, status: 'authenticated' } : { user: null, status: 'guest' })
  },

  signIn: async (username, password) => {
    const demo = matchDemoLogin(username, password) // client-side demo accounts only
    if (demo) {
      rememberDemo(demo.email)
      set({ user: demo.user, status: 'authenticated' })
      return { ok: true, isNew: false }
    }
    const res = await authApi.login(username, password)
    if (!res.loginResult) return { ok: false, isNew: false }
    const user = await authApi.getCurrentUser()
    set({ user, status: user ? 'authenticated' : 'guest' })
    return { ok: true, isNew: res.isNew }
  },

  signOut: async () => {
    if (storedDemo()) {
      clearDemo() // demo session: no server call
      set({ user: null, status: 'guest' })
      return
    }
    await authApi.logout()
    set({ user: null, status: 'guest' })
  },

  refresh: async () => {
    const demo = storedDemo()
    if (demo) {
      set({ user: demo.user, status: 'authenticated' })
      return
    }
    const user = await authApi.getCurrentUser()
    set({ user, status: user ? 'authenticated' : 'guest' })
  },

  setUser: (user) => set({ user, status: user ? 'authenticated' : 'guest' }),
}))
